import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { CalendarDays, Clock, Car, Loader2, Check, AlertCircle, Repeat, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, token, isPremium } = useAuth();
  const { t, language } = useLanguage();

  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(searchParams.get("service") || "");
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [notes, setNotes] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState("");
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const locale = language === "de" ? de : enUS;

  const vehicleTypes = [
    { id: "kleinwagen", label: language === "de" ? "Kleinwagen" : "Compact Car" },
    { id: "limousine", label: language === "de" ? "Limousine" : "Sedan" },
    { id: "kombi", label: language === "de" ? "Kombi" : "Station Wagon" },
    { id: "suv", label: "SUV / " + (language === "de" ? "Geländewagen" : "SUV") },
    { id: "van", label: "Van / " + (language === "de" ? "Transporter" : "Van") },
  ];

  const recurringIntervals = [
    { id: "weekly", label: t("booking_weekly") },
    { id: "biweekly", label: t("booking_biweekly") },
    { id: "monthly", label: t("booking_monthly") },
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/available-slots/${date}`);
      setAvailableSlots(response.data.available_slots);
      setSelectedSlot("");
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error(t("booking_login_required"));
      navigate("/login?redirect=/booking");
      return;
    }

    if (!selectedService || !selectedDate || !selectedSlot || !vehicleType) {
      toast.error(language === "de" ? "Bitte füllen Sie alle Pflichtfelder aus." : "Please fill in all required fields.");
      return;
    }

    if (isRecurring && !recurringInterval) {
      toast.error(language === "de" ? "Bitte wählen Sie ein Intervall." : "Please select an interval.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/bookings`,
        {
          service_id: selectedService,
          date: format(selectedDate, "yyyy-MM-dd"),
          time_slot: selectedSlot,
          vehicle_type: vehicleType,
          notes: notes || null,
          is_recurring: isRecurring,
          recurring_interval: isRecurring ? recurringInterval : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(language === "de" ? "Termin erfolgreich gebucht!" : "Appointment booked successfully!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || (language === "de" ? "Buchung fehlgeschlagen." : "Booking failed.");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedServiceData = services.find((s) => s.id === selectedService);
  const discountedPrice = selectedServiceData
    ? isPremium
      ? (selectedServiceData.price * 0.85).toFixed(2)
      : selectedServiceData.price.toFixed(2)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="booking-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 font-['Chivo']">
            {t("booking_title").split(" ")[0]} <span className="text-cyan-400">{t("booking_title").split(" ")[1]}</span>
          </h1>
          <p className="text-zinc-400">
            {language === "de" 
              ? "Wählen Sie Ihren Service, Datum und Uhrzeit für Ihren Termin."
              : "Select your service, date, and time for your appointment."}
          </p>
        </div>

        {!isAuthenticated && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-200">
              {t("booking_login_required")}{" "}
              <a href="/login" className="text-cyan-400 hover:underline">{t("nav_login")}</a>
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Service Selection */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Car className="h-5 w-5 text-cyan-400" />
                {t("booking_select_service")}
              </h2>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white" data-testid="service-select">
                  <SelectValue placeholder={language === "de" ? "Service auswählen" : "Select service"} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id} className="text-white hover:bg-zinc-700">
                      <div className="flex items-center justify-between w-full">
                        <span>{language === "de" ? service.name_de : service.name}</span>
                        <span className="ml-4 text-zinc-400">ab {service.price.toFixed(2)}€</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedServiceData && (
                <div className="mt-4 p-3 bg-zinc-800/50 rounded-sm flex items-center justify-between">
                  <span className="text-sm text-zinc-400 flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-400" />
                    {t("services_points")}:
                  </span>
                  <span className="text-sm font-medium text-cyan-400">
                    +{selectedServiceData.loyalty_points_earned} {isPremium && "(2x = " + (selectedServiceData.loyalty_points_earned * 2) + ")"}
                  </span>
                </div>
              )}
            </div>

            {/* Date Selection */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-cyan-400" />
                {t("booking_select_date")}
              </h2>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={locale}
                  disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                  className="rounded-sm border border-zinc-700 bg-zinc-800"
                  data-testid="booking-calendar"
                />
              </div>
            </div>

            {/* Time Selection */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-cyan-400" />
                {t("booking_select_time")}
              </h2>
              {!selectedDate ? (
                <p className="text-zinc-500 text-sm">
                  {language === "de" ? "Bitte wählen Sie zuerst ein Datum." : "Please select a date first."}
                </p>
              ) : slotsLoading ? (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {language === "de" ? "Verfügbare Zeiten werden geladen..." : "Loading available times..."}
                </div>
              ) : availableSlots.length === 0 ? (
                <p className="text-zinc-500 text-sm">
                  {language === "de" ? "Keine Termine an diesem Tag verfügbar." : "No appointments available on this day."}
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot}
                      variant={selectedSlot === slot ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedSlot(slot)}
                      className={
                        selectedSlot === slot
                          ? "bg-cyan-500 text-black hover:bg-cyan-400"
                          : "border-zinc-700 text-zinc-300 hover:border-cyan-500 hover:text-white"
                      }
                      data-testid={`slot-${slot}`}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Vehicle Type */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4">{t("booking_vehicle_type")}</h2>
              <Select value={vehicleType} onValueChange={setVehicleType}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white" data-testid="vehicle-select">
                  <SelectValue placeholder={language === "de" ? "Fahrzeugtyp auswählen" : "Select vehicle type"} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {vehicleTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id} className="text-white hover:bg-zinc-700">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recurring Booking */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Repeat className="h-5 w-5 text-cyan-400" />
                  {t("booking_recurring")}
                </h2>
                <Switch
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                  data-testid="recurring-switch"
                />
              </div>
              
              {isRecurring && (
                <div className="space-y-4">
                  <Select value={recurringInterval} onValueChange={setRecurringInterval}>
                    <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white" data-testid="recurring-interval-select">
                      <SelectValue placeholder={t("booking_recurring_interval")} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {recurringIntervals.map((interval) => (
                        <SelectItem key={interval.id} value={interval.id} className="text-white hover:bg-zinc-700">
                          {interval.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    {language === "de" 
                      ? "Es werden automatisch 3 weitere Termine für Sie erstellt."
                      : "3 additional appointments will be created for you automatically."}
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4">{t("booking_notes")}</h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={language === "de" ? "Besondere Wünsche oder Hinweise..." : "Special requests or notes..."}
                className="w-full h-24 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-sm text-white placeholder-zinc-500 resize-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                data-testid="notes-input"
              />
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-white mb-4">{t("booking_summary")}</h2>
              
              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Service</span>
                  <span className="text-white text-right max-w-[150px] truncate">
                    {selectedServiceData ? (language === "de" ? selectedServiceData.name_de : selectedServiceData.name) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">{t("common_date")}</span>
                  <span className="text-white">
                    {selectedDate ? format(selectedDate, "dd. MMMM yyyy", { locale }) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">{t("common_time")}</span>
                  <span className="text-white">{selectedSlot || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">{t("booking_vehicle_type")}</span>
                  <span className="text-white">
                    {vehicleTypes.find((v) => v.id === vehicleType)?.label || "—"}
                  </span>
                </div>
                {isRecurring && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t("booking_recurring")}</span>
                    <span className="text-cyan-400">
                      {recurringIntervals.find((i) => i.id === recurringInterval)?.label || "—"}
                    </span>
                  </div>
                )}

                <div className="border-t border-zinc-700 pt-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-zinc-500">{t("booking_price")}</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-white price-tag">
                        {discountedPrice ? `${discountedPrice}€` : "—"}
                      </span>
                      {isPremium && selectedServiceData && (
                        <div className="text-xs text-cyan-400 mt-1">
                          15% Premium-Rabatt
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting || !selectedService || !selectedDate || !selectedSlot || !vehicleType}
                className="w-full mt-6 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-booking-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {language === "de" ? "Wird gebucht..." : "Booking..."}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t("booking_confirm")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
